import React, { useState, useMemo } from 'react';
import {
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Check,
  Close,
  MoreVert,
  Search,
  RemoveCircleOutlined,
  OpenInNew,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  useGetUserGatesQuery,
  useAddStatsigGateOverrideMutation,
  useRemoveStatsigGateOverrideMutation,
} from '../store/api';
import type { UserGateStatusResponse } from '@store/generated/admin';
import { StatsigGateRow } from './statsig-gate-row';

const ORG_ID_TYPE = 'organizationID';

interface ProviderStatsigGatesProps {
  providerId: string;
  /** Provider's organization id — required to evaluate org-scoped gates and link to the org page. */
  organizationId?: string;
}

export function ProviderStatsigGates({
  providerId,
  organizationId,
}: ProviderStatsigGatesProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Passing organizationId lets the server evaluate org-scoped gates against the
  // provider's real org rather than falling back to the user id.
  const { data: userGates, isLoading } = useGetUserGatesQuery(
    { userId: providerId, organizationId: organizationId || undefined },
    { skip: !providerId },
  );

  const gates = userGates?.gates ?? [];

  const filteredGates = useMemo(() => {
    if (!searchQuery) return gates;
    const query = searchQuery.toLowerCase();
    return gates.filter(
      gate =>
        gate.name.toLowerCase().includes(query) ||
        gate.gate_id.toLowerCase().includes(query) ||
        gate.description?.toLowerCase().includes(query),
    );
  }, [gates, searchQuery]);

  const userScopedGates = filteredGates.filter(gate => gate.id_type !== ORG_ID_TYPE);
  const orgScopedGates = filteredGates.filter(gate => gate.id_type === ORG_ID_TYPE);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <CircularProgress size={24} />
      </div>
    );
  }

  if (!gates.length) {
    return <div className="p-4 text-gray-500 text-sm">No Statsig feature gates available</div>;
  }

  return (
    <div>
      {/* Search */}
      <div className="mb-4">
        <TextField
          size="small"
          placeholder="Search gates..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" className="text-gray-400" />
                </InputAdornment>
              ),
            },
          }}
        />
      </div>

      {/* User-scoped gates (interactive overrides) */}
      <div className="space-y-1">
        {userScopedGates.map(gate => (
          <UserGateRow key={gate.gate_id} gate={gate} providerId={providerId} />
        ))}
      </div>

      {/* Org-scoped gates (read-only — managed on the org page) */}
      {orgScopedGates.length > 0 && (
        <div className="mt-6">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Organization gates (read-only)
          </h4>
          <p className="text-xs text-gray-400 mb-2">
            These gates are scoped to this provider&apos;s organization. Overrides are
            managed on the organization page.
          </p>
          <div className="space-y-1">
            {orgScopedGates.map(gate => (
              <StatsigGateRow
                key={gate.gate_id}
                name={gate.name}
                description={gate.description}
                isEnabled={gate.is_enabled}
                evaluatedResult={gate.evaluated_result}
                overrideStatus={gate.override_status}
                actions={
                  organizationId ? (
                    <RouterLink
                      to={`/admin-v2/organization/${organizationId}`}
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                      title="Manage on the organization page"
                    >
                      <OpenInNew sx={{ fontSize: 14 }} />
                      Org page
                    </RouterLink>
                  ) : null
                }
              />
            ))}
          </div>
        </div>
      )}

      {filteredGates.length === 0 && (
        <div className="p-4 text-gray-500 text-sm text-center">
          No gates match &quot;{searchQuery}&quot;
        </div>
      )}
    </div>
  );
}

// Interactive user-scoped gate row (userID / stableID gates)
interface UserGateRowProps {
  gate: UserGateStatusResponse;
  providerId: string;
}

function UserGateRow({ gate, providerId }: UserGateRowProps) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [addOverride, { isLoading: isAddingOverride }] = useAddStatsigGateOverrideMutation();
  const [removeOverride, { isLoading: isRemovingOverride }] =
    useRemoveStatsigGateOverrideMutation();

  const isUpdating = isAddingOverride || isRemovingOverride;

  const handleSetOverride = async (value: 'pass' | 'fail') => {
    setMenuAnchor(null);
    try {
      await addOverride({
        gateId: gate.gate_id,
        addGateOverridesRequest: {
          passing_user_ids: value === 'pass' ? [providerId] : null,
          failing_user_ids: value === 'fail' ? [providerId] : null,
        },
      }).unwrap();
    } catch (error) {
      console.error('Failed to set gate override:', error);
    }
  };

  const handleRemoveOverride = async () => {
    setMenuAnchor(null);
    try {
      await removeOverride({
        gateId: gate.gate_id,
        removeGateOverridesRequest: {
          passing_user_ids: [providerId],
          failing_user_ids: [providerId],
        },
      }).unwrap();
    } catch (error) {
      console.error('Failed to remove gate override:', error);
    }
  };

  return (
    <StatsigGateRow
      name={gate.name}
      description={gate.description}
      isEnabled={gate.is_enabled}
      evaluatedResult={gate.evaluated_result}
      overrideStatus={gate.override_status}
      actions={
        <>
          <IconButton
            size="small"
            onClick={e => setMenuAnchor(e.currentTarget)}
            disabled={isUpdating}
            aria-label="Feature gate override actions"
          >
            <MoreVert fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem onClick={() => handleSetOverride('pass')} disabled={isUpdating}>
              <Check className="mr-2 text-green-600" fontSize="small" />
              Force On
            </MenuItem>
            <MenuItem onClick={() => handleSetOverride('fail')} disabled={isUpdating}>
              <Close className="mr-2 text-red-600" fontSize="small" />
              Force Off
            </MenuItem>
            {gate.override_status && (
              <MenuItem onClick={handleRemoveOverride} disabled={isUpdating}>
                <RemoveCircleOutlined className="mr-2 text-gray-600" fontSize="small" />
                Clear override (use default)
              </MenuItem>
            )}
          </Menu>
        </>
      }
    />
  );
}
